import { useEffect, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Search, SlidersHorizontal } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import {
  dealerSelected,
  fetchDealers,
  fetchSubDealers,
  filtersCleared,
  roleFilterChanged,
  searchChanged,
  selectVisibleDealers,
  statusFilterChanged,
  type RoleFilter,
  type StatusFilter
} from '@/features/dealers/dealersSlice'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, Spinner } from '@/components/ui/Surface'
import { Button } from '@/components/ui/Button'
import { SelectInput, TextInput } from '@/components/ui/Field'
import { DealerTable } from './DealerTable'
import { DealerDrawer } from './DealerDrawer'
import { dealerRole } from '@/lib/format'

export function DealersPage(): ReactNode {
  const dispatch = useAppDispatch()
  const dealers = useAppSelector(selectVisibleDealers)
  const { items, loading, error, search, role, status } = useAppSelector((s) => s.dealers)

  const filtered = search.trim() !== '' || role !== 'all' || status !== 'all'

  // Leaving the page closes the detail panel, so coming back lands on the list
  // rather than reopening whoever was last inspected.
  useEffect(() => {
    return () => {
      dispatch(dealerSelected(null))
    }
  }, [dispatch])

  const open = (userId: string): void => {
    dispatch(dealerSelected(userId))
    const dealer = items.find((d) => d.user_id === userId)
    // Only a primary account owns sub-dealers; asking for anyone else 400s.
    if (dealer && dealerRole(dealer) === 'primary') {
      void dispatch(fetchSubDealers(userId))
    }
  }

  return (
    <>
      <PageHeader
        title="Dealers"
        description="Every registered account, with its subscription and client limit."
        actions={
          <Button
            variant="secondary"
            icon={RefreshCw}
            loading={loading}
            onClick={() => void dispatch(fetchDealers())}
          >
            Refresh
          </Button>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <div className="min-w-[220px] flex-1">
            <TextInput
              icon={Search}
              value={search}
              onChange={(e) => dispatch(searchChanged(e.target.value))}
              placeholder="Search name, email or dealer ID…"
              aria-label="Search dealers"
            />
          </div>

          {/* Controls are full-width by default, so the wrapper sets the size. */}
          <div className="w-36">
            <SelectInput
              value={role}
              onChange={(e) => dispatch(roleFilterChanged(e.target.value as RoleFilter))}
              aria-label="Filter by role"
            >
              <option value="all">All roles</option>
              <option value="primary">Primary</option>
              <option value="sub">Sub-dealer</option>
              <option value="admin">Admin</option>
            </SelectInput>
          </div>

          <div className="w-40">
            <SelectInput
              value={status}
              onChange={(e) => dispatch(statusFilterChanged(e.target.value as StatusFilter))}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              <option value="paid">On a plan</option>
              <option value="trial">Trial</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
              <option value="admin">Admin</option>
            </SelectInput>
          </div>

          {filtered && (
            <Button
              variant="ghost"
              size="sm"
              icon={SlidersHorizontal}
              onClick={() => dispatch(filtersCleared())}
            >
              Clear
            </Button>
          )}

          <span className="label-caps ml-auto whitespace-nowrap px-1 text-subtle">
            {dealers.length} / {items.length}
          </span>
        </div>

        {error ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-negative/10 text-negative ring-1 ring-inset ring-negative/30">
              <AlertTriangle size={18} aria-hidden />
            </span>
            <p className="text-[13px] font-semibold text-fg">Could not load dealers</p>
            <p className="max-w-sm text-[12px] text-muted">{error}</p>
            <Button
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              onClick={() => void dispatch(fetchDealers())}
            >
              Try again
            </Button>
          </div>
        ) : loading && items.length === 0 ? (
          <Spinner label="Loading dealers" />
        ) : (
          <DealerTable
            dealers={dealers}
            totalCount={items.length}
            onSelect={open}
            onClearFilters={() => dispatch(filtersCleared())}
          />
        )}
      </Card>

      <DealerDrawer />
    </>
  )
}
